import React, { useEffect, useState } from "react";
import axios from "axios";
import { Heart, ShoppingCart } from "lucide-react";
import freeDelivery from "../assets/images/productDetails/FreeDelivery.png"
import freeReturn from "../assets/images/productDetails/FreeReturn.png"
import securePay from "../assets/images/productDetails/SecurePay.png"
import { GrShareOption } from "react-icons/gr";
import { TiStarFullOutline } from "react-icons/ti";
import SimilarProducts from "./SimilarProducts";
import SummaryApi from "../common/SummaryApi";
import { useParams, useNavigate } from "react-router-dom";
import Breadcrumb from "../components/BreadCrumb";
import { addToLocalWishlist, removeFromLocalWishlist, isInLocalWishlist } from "../helpers/wishlistHelper";
import { SlLike } from "react-icons/sl";
import { MdOutlineQuickreply } from "react-icons/md";
import { addToCart } from "../helpers/cartHelper";
import { toast } from "react-hot-toast";
import { truncateText } from "../helpers/truncate";
import { useCart } from "../context/CartContext";
import navigateToCheckout from "../helpers/navigateToCheckout";
import { normalizeCartItem } from "../helpers/cartNormalizer";

import { FaFacebook, FaWhatsapp, FaInstagram } from "react-icons/fa"; // Brand Icons
import { FaXTwitter } from "react-icons/fa6";
import { Copy, X } from "lucide-react"; // Add Instagram here

// Base data stored in INCHES (important)
const sizeChartData = [
  { size: "XS", chest: 36, length: 24, shoulder: 16 },
  { size: "S", chest: 38, length: 25, shoulder: 17 },
  { size: "M", chest: 40, length: 26, shoulder: 18 },
  { size: "L", chest: 42, length: 27, shoulder: 19 },
  { size: "XL", chest: 44, length: 28, shoulder: 20 },
  { size: "XXL", chest: 46, length: 29, shoulder: 21 },
  { size: "3XL", chest: 48, length: 30, shoulder: 22 },
  { size: "4XL", chest: 50, length: 31, shoulder: 23 },
  { size: "5XL", chest: 52, length: 32, shoulder: 24 },
  { size: "6XL", chest: 54, length: 33, shoulder: 25 },
  { size: "7XL", chest: 56, length: 34, shoulder: 26 },
  { size: "8XL", chest: 58, length: 35, shoulder: 27 },
  { size: "9XL", chest: 60, length: 36, shoulder: 28 },
  { size: "10XL", chest: 62, length: 37, shoulder: 29 },
];

const ProductDetailsSkeleton = () => {
  return (
    <div className="container mx-auto px-4 py-6 animate-pulse">

      {/* BREADCRUMB */}
      <div className="h-4 w-64 bg-gray-200 rounded mb-6 lg:hidden" />

      {/* TOP SECTION */}
      <div className="grid lg:grid-cols-2 gap-10">

        {/* LEFT IMAGE SECTION */}
        <div>
          {/* MAIN IMAGE */}
          <div className="h-[480px] rounded-xl bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200" />

          {/* THUMBNAILS */}
          <div className="flex gap-3 mt-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="w-20 h-20 rounded-xl bg-gray-200"
              />
            ))}
          </div>
        </div>

        {/* RIGHT CONTENT */}
        <div className="space-y-5">

          {/* TITLE */}
          <div className="h-7 bg-gray-200 rounded w-4/5" />

          {/* BRAND */}
          <div className="h-4 bg-gray-200 rounded w-1/3" />

          {/* PRICE */}
          <div className="flex gap-3 items-center">
            <div className="h-5 w-20 bg-gray-200 rounded" />
            <div className="h-7 w-28 bg-gray-200 rounded" />
          </div>

          {/* RATING */}
          <div className="h-4 w-24 bg-gray-200 rounded" />

          {/* COLORS */}
          <div>
            <div className="h-3 w-24 bg-gray-200 rounded mb-3" />
            <div className="flex gap-3">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="w-9 h-9 rounded-full bg-gray-200"
                />
              ))}
            </div>
          </div>

          {/* SIZES */}
          <div>
            <div className="h-3 w-24 bg-gray-200 rounded mb-3" />
            <div className="flex gap-2">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="w-11 h-11 rounded-md bg-gray-200"
                />
              ))}
            </div>
          </div>

          {/* BUTTONS */}
          <div className="flex gap-3 mt-4">
            <div className="flex-1 h-12 rounded-lg bg-gray-200" />
            <div className="w-12 h-12 rounded-lg bg-gray-200" />
          </div>

          {/* DELIVERY ICONS */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-24 rounded-2xl bg-gray-200"
              />
            ))}
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className="flex gap-4 mt-12">
        <div className="h-10 w-40 bg-gray-200 rounded-lg" />
        <div className="h-10 w-40 bg-gray-200 rounded-lg" />
      </div>

      {/* BOTTOM CONTENT */}
      <div className="grid lg:grid-cols-2 gap-8 mt-6">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="border rounded-xl p-4 space-y-3">
            <div className="h-4 w-40 bg-gray-200 rounded" />
            {[...Array(4)].map((_, j) => (
              <div
                key={j}
                className="h-3 w-full bg-gray-200 rounded"
              />
            ))}
          </div>
        ))}
      </div>

      {/* REVIEWS SECTION */}
      <div className="mt-10 space-y-6">
        <div className="h-6 w-48 bg-gray-200 rounded" />

        {[...Array(3)].map((_, i) => (
          <div key={i} className="border-b pb-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-40 bg-gray-200 rounded" />
                <div className="h-3 w-24 bg-gray-200 rounded" />
                <div className="h-3 w-full bg-gray-200 rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* SIMILAR PRODUCTS */}
      <div className="mt-12">
        <div className="h-6 w-48 bg-gray-200 rounded mb-6" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-64 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>

    </div>
  );
};




const ShareModal = ({ isOpen, onClose, productUrl, productName }) => {
  if (!isOpen) return null;

  // 1. WhatsApp Share Logic
  const shareToWhatsApp = () => {
    const message = `Check out this product: ${productName} \n${productUrl}`;
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  // 2. Facebook Share Logic
  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}`;
    window.open(url, "_blank");
  };

  // 3. Twitter (X) Share Logic
  const shareToTwitter = () => {
    const text = `Check out ${productName}`;
    const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(productUrl)}&text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  // 4. Instagram Logic (Copy Link + Open)
  const shareToInstagram = () => {
    navigator.clipboard.writeText(productUrl);
    toast.success("Link copied! Paste it on Instagram.");
    window.open("https://www.instagram.com/", "_blank");
  };

  // 5. Copy Link Logic
  const handleCopy = () => {
    navigator.clipboard.writeText(productUrl);
    toast.success("Link copied!");
    onClose();
  };

  // 6. Native Mobile Share (Optional but Recommended)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: productName,
          text: `Check out this product: ${productName}`,
          url: productUrl,
        });
        onClose();
      } catch (error) {
        console.log("Error sharing", error);
      }
    } else {
      toast.error("Web Share not supported on this browser");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl w-full max-w-sm p-6 shadow-2xl relative animate-in fade-in zoom-in duration-200"
        onClick={e => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute right-4 top-4 text-gray-400 hover:text-gray-600">
          <X size={20} />
        </button>

        <h3 className="text-lg font-bold text-gray-800 mb-6 text-center">Share this product</h3>

        <div className="flex justify-center gap-4 mb-8 flex-wrap">

          {/* WhatsApp */}
          <button onClick={shareToWhatsApp}>
            <div className="flex flex-col items-center gap-2 group">
              <div className="w-[64px] h-[64px] flex items-center justify-center bg-green-50 rounded-full group-hover:bg-green-100 transition-colors">
                <FaWhatsapp size={32} className="text-[#25D366]" />
              </div>
            </div>
          </button>

          {/* Facebook */}
          <button onClick={shareToFacebook}>
            <div className="flex flex-col items-center gap-2 group">
              <div className="w-[64px] h-[64px] flex items-center justify-center bg-blue-50 rounded-full group-hover:bg-blue-100 transition-colors">
                <FaFacebook size={32} className="text-[#1877F2]" />
              </div>
            </div>
          </button>

          {/* X (Twitter) */}
          <button onClick={shareToTwitter}>
            <div className="flex flex-col items-center gap-2 group">
              {/* Changed bg to Gray-100 and hover to Gray-200 for X branding */}
              <div className="w-[64px] h-[64px] flex items-center justify-center bg-gray-100 rounded-full group-hover:bg-gray-200 transition-colors">
                <FaXTwitter size={30} className="text-black" />
              </div>
            </div>
          </button>

          {/* Instagram */}
          <button onClick={shareToInstagram}>
            <div className="flex flex-col items-center gap-2 group">
              <div className="w-[64px] h-[64px] flex items-center justify-center bg-pink-50 rounded-full group-hover:bg-pink-100 transition-colors">
                <FaInstagram size={32} className="text-[#E1306C]" />
              </div>
            </div>
          </button>

        </div>

        {/* Copy Link Section */}
        <div className="relative">
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 truncate pr-12">
            {productUrl}
          </div>
          <button
            onClick={handleCopy}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-200 rounded-md transition-colors"
            title="Copy Link"
          >
            <Copy size={16} className="text-gray-600" />
          </button>
        </div>

        {/* OPTIONAL: Add a generic "Share via..." button for mobile native sheet */}
        {navigator.share && (
          <button
            onClick={handleNativeShare}
            className="w-full mt-4 py-2 text-sm font-semibold text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50"
          >
            More Options...
          </button>
        )}

      </div>
    </div>
  );
};


const SizeChartModal = ({ onClose, sizeChartData }) => {
  const [unit, setUnit] = useState("in");

  const convert = (v) => {
    if (!v) return "-";
    return unit === "cm" ? (v * 2.54).toFixed(1) : v;
  };

  return (
    // OVERLAY (clicking here closes modal)
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4"
      onClick={onClose}
    >
      {/* MODAL BOX (clicking inside should NOT close) */}
      <div
        className="bg-white w-full max-w-3xl rounded-xl overflow-hidden flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="flex justify-between items-center px-6 py-4 border-b shrink-0 z-20 bg-white">
          <h2 className="text-lg font-semibold">Size Chart</h2>

          <div className="flex gap-3 items-center">
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="border px-2 py-1 rounded-md text-sm outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="in">in</option>
              <option value="cm">cm</option>
            </select>

            <button onClick={onClose} className="text-xl p-1 hover:bg-gray-100 rounded-full transition-colors">✕</button>
          </div>
        </div>

        {/* TABLE WRAPPER - SCROLLABLE Y-AXIS */}
        {/* FIX: Changed 'p-6' to 'px-6 pb-6'. Removed top padding so sticky header sits flush at the top */}
        <div className="px-6 pb-6 overflow-y-auto custom-scrollbar">
          {sizeChartData.length === 0 ? (
            <p className="text-center text-sm text-gray-500 mt-6">
              Size chart not available
            </p>
          ) : (
            <table className="w-full border border-blue-100 relative mt-6">
              {/* Added mt-6 to table instead of wrapper padding, but be careful with sticky. 
                  Better approach: Keep sticky top-0 and use background to cover. 
                  Since we removed wrapper top padding, we can use 'top-0' on thead directly. */}

              <thead className="bg-blue-50 text-sm sticky top-0 z-10 shadow-sm">
                <tr>
                  <th className="p-3 border bg-blue-50">Size</th>
                  <th className="p-3 border bg-blue-50">Chest ({unit})</th>
                  <th className="p-3 border bg-blue-50">Length ({unit})</th>
                  <th className="p-3 border bg-blue-50">Shoulder ({unit})</th>
                </tr>
              </thead>

              <tbody className="text-center text-sm">
                {sizeChartData.map((row, i) => (
                  <tr key={i} className="hover:bg-blue-50/30 transition-colors">
                    <td className="p-3 border font-medium bg-gray-50/50">
                      {row.size}
                    </td>
                    <td className="p-3 border">{convert(row.chest)}</td>
                    <td className="p-3 border">{convert(row.length)}</td>
                    <td className="p-3 border">{convert(row.shoulder)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};


const slugify = (text = "") =>
  text
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const ProductDetails = () => {
  const { refreshCart } = useCart();
  const navigate = useNavigate();
  const { productId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [variant, setVariant] = useState(null);
  const [size, setSize] = useState(null);
  const [image, setImage] = useState("");
  const [openSizeChart, setOpenSizeChart] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [isWishlisted, setIsWishlisted] = useState(false);
  const token = localStorage.getItem("token"); // or from redux
  const isLoggedIn = Boolean(token);
  const [reviews, setReviews] = useState([]);
  const [reviewsSummary, setReviewsSummary] = useState({
    rating: 0,
    total: 0,
    breakdown: [],
  });

  // Share Modal State
  const [openShare, setOpenShare] = useState(false);
  const productUrl = window.location.href;

  useEffect(() => {
    if (!productId) return;

    const checkWishlistStatus = async () => {
      try {
        if (!isLoggedIn) {
          // ✅ Guest
          setIsWishlisted(isInLocalWishlist(productId));
          return;
        }

        // ✅ Logged-in user → check backend
        const res = await axios({
          url: SummaryApi.getWishlist.url,
          method: SummaryApi.getWishlist.method,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const exists = res.data.wishlist.some(
          (item) => item.productId._id === productId
        );

        setIsWishlisted(exists);

      } catch (err) {
        console.error("Wishlist check failed", err);
      }
    };

    checkWishlistStatus();
  }, [productId, isLoggedIn, token]);


  const hasValue = (v) =>
    v !== null && v !== undefined && v !== "" &&
    (!Array.isArray(v) || v.length > 0);

  const hasValidSizes = (sizes = []) =>
    sizes.some(
      (s) => typeof s.size === "string" && s.size.trim() !== ""
    );

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const res = await axios({
          url: SummaryApi.getOneProduct.url(productId),
          method: SummaryApi.getOneProduct.method,
        });

        const productData = res.data;
        setData(productData);

        if (productData?.variants?.length) {
          const firstVariant = productData.variants[0];
          setVariant(firstVariant);

          if (firstVariant?.sizes?.length) {
            setSize(firstVariant.sizes[0]);
          }

          if (firstVariant?.images?.length) {
            setImage(firstVariant.images[0]);
          }
        }
      } catch (error) {
        console.error("API Error:", error);
      } finally {
        setLoading(false);
      }
    };

    if (productId) fetchProduct();
  }, [productId]);

  useEffect(() => {
    if (!productId) return;

    const fetchReviews = async () => {
      try {
        const res = await axios({
          url: SummaryApi.getProductReviews.url(productId),
          method: SummaryApi.getProductReviews.method,
        });

        if (res.data.success) {
          const reviewsData = res.data.reviews;
          setReviews(reviewsData);

          // --------- CALCULATE SUMMARY ----------
          const total = reviewsData.length;

          if (total === 0) {
            setReviewsSummary({
              rating: 0,
              total: 0,
              breakdown: [],
            });
            return;
          }

          const sum = reviewsData.reduce((a, r) => a + r.rating, 0);
          const avg = (sum / total).toFixed(1);

          const breakdown = [5, 4, 3, 2, 1].map((star) => {
            const count = reviewsData.filter(r => r.rating === star).length;
            return {
              star,
              percent: Math.round((count / total) * 100),
            };
          });

          setReviewsSummary({
            rating: avg,
            total,
            breakdown,
          });
        }
      } catch (err) {
        console.error("Fetch reviews failed", err);
      }
    };

    fetchReviews();
  }, [productId]);

  const handleWishlistToggle = async () => {
    try {
      if (!isLoggedIn) {
        // GUEST USER
        if (isInLocalWishlist(productId)) {
          removeFromLocalWishlist(productId);
          setIsWishlisted(false);
        } else {
          addToLocalWishlist(productId);
          setIsWishlisted(true);
        }
        return;
      }

      // LOGGED-IN USER
      const res = await axios({
        url: SummaryApi.toggleWishlist.url,
        method: SummaryApi.toggleWishlist.method,
        data: { productId },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setIsWishlisted(res.data.action === "added");

    } catch (err) {
      console.error("Wishlist error", err);
    }
  };

  const handleBuyNow = () => {
    if (!variant || !size) {
      toast.error("Please select size");
      return;
    }

    // 🔍 ADAPTER OBJECT (match cart structure)
    const rawItem = {
      product: {
        _id: data.product._id,
        name: data.product.productName,
      },
      variant: {
        _id: variant._id,
        colorCode: variant.colorCode,
        images: variant.images,
      },
      size: {
        _id: size._id,
        size: size.size || "",
        price: size.price,
        mrp: size.mrp,
        stock: size.stock,
      },
      quantity: 1,
    };

    // ✅ normalize same as cart
    const normalizedItem = normalizeCartItem(rawItem);

    // ✅ navigate to checkout
    navigateToCheckout(
      navigate,
      [normalizedItem],
      "direct"
    );
  };

  const handleAddToCart = async () => {
    if (!size || !variant) {
      toast.error("Please select size");
      return;
    }

    try {
      const res = await addToCart({
        productId,
        variantId: variant._id,
        sizeId: size._id,
        quantity: 1,
        token
      });

      toast.success(
        res.mode === "guest"
          ? "Added to cart (Guest)"
          : "Added to cart"
      );
      refreshCart();
      setTimeout(() => {
        navigate("/cart-page");
      }, 600);

    } catch (err) {
      toast.error("Failed to add to cart");
      console.error(err);
    }
  };

  // Update size & image when variant changes
  useEffect(() => {
    if (variant) {
      setSize(variant.sizes[0]);
      setImage(variant.images[0]);
    }
  }, [variant]);

  //  Prevent render until data loaded
  if (loading) {
    return <ProductDetailsSkeleton />;
  }

  const discount = Math.round(
    ((size.mrp - size.price) / size.mrp) * 100
  );

  const breadcrumbItems = [
    { label: "Home", path: "/" },

    data.product.topCategory?.name && {
      label: data.product.topCategory.name,
      path: `/all-userproducts/${slugify(data.product.topCategory.name)}`
    },

    data.product.subCategory?.name && {
      label: data.product.subCategory.name,
      path: `/all-userproducts/${slugify(data.product.subCategory.name)}`
    },

    {
      label: truncateText(data.product.productName, 12, 7),
      path: null // current page
    }
  ].filter(Boolean);



  return (
    <div className=" bg-[#FFFFFF]">

      <div className="container mx-auto px-4 py-6">
        {/* Breadcrumb */}
        {/* MOBILE & TAB BREADCRUMB */}
        <div className="block lg:hidden mb-4">
          <Breadcrumb items={breadcrumbItems} />
        </div>
        <div className="grid lg:grid-cols-2 gap-10">

          {/* LEFT IMAGE */}
          {/* IMAGE GALLERY */}
          <div className="w-full">

            {/* MAIN IMAGE */}
            {hasValue(image) && (
              <div className="relative bg-gray-500 rounded-xl overflow-hidden">
                <img
                  src={image}
                  alt=""
                  className="w-full h-[380px] md:h-[520px] object-contain bg-[#eff4ff]"
                />

                {/* Wishlist */}
                <button
                  onClick={handleWishlistToggle}
                  className="absolute top-4 right-4 w-9 h-9 bg-white rounded-full shadow flex items-center justify-center">
                  <Heart
                    size={16}
                    className={
                      isWishlisted
                        ? "text-red-500 fill-red-500"
                        : "text-gray-600"
                    }
                  />
                </button>
              </div>
            )}
            {/* THUMBNAILS */}
            {hasValue(variant.images) && (
              <div className="flex gap-3 mt-4">
                {variant.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setImage(img)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border ${image === img ? "border-[#007BFF]" : "border-gray-200"
                      }`}
                  >
                    <img alt="" src={img} className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}

          </div>

          {/* RIGHT CONTENT */}
          <div>


            {/* DESKTOP BREADCRUMB */}
            <div className="hidden lg:inline-flex mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            {/* TITLE + SHARE */}
            <div className="flex items-start justify-between mb-1">
              <h1 className="text-2xl font-semibold">
                {data.product.productName}
              </h1>

              <button
                onClick={() => setOpenShare(true)}
                className="p-2 text-[#5C5F6A] hover:text-gray-800"
              >
                {/* Share Icon */}
                <GrShareOption className="w-5 h-5" />
              </button>
            </div>

            {/* SUB TITLE */}
            <p className="text-sm text-[#B9BBBF] mb-3">
              {data.product.brand}
            </p>

            {/* PRICE SECTION */}
            <div className="flex items-center gap-3">
              {hasValue(size?.mrp) && (
                <p className="text-sm text-[#666666] line-through">
                  MRP ₹{size.mrp}
                </p>
              )}

              <div className="flex items-center gap-3">
                {hasValue(size?.price) && (
                  <span className="text-2xl font-medium text-gray-900">
                    ₹{size.price}
                  </span>
                )}
              </div>
            </div>

            {/* DISCOUNT + RATING */}
            <div className="flex items-center gap-4 mb-5">
              {size?.mrp > size?.price && (
                <span className="text-[#21C45D] text-sm font-medium">
                  {discount}% Off
                </span>
              )}

              <div className="flex items-center gap-1 text-sm text-gray-700">
                <TiStarFullOutline className="text-[#FFA439] w-4 h-4" />
                <span className="font-medium">{data.product.ratings?.avgRating}</span>
              </div>
            </div>

            {/* COLORS */}
            {Array.isArray(data.variants) && data.variants.length > 0 && (
              <div className="mb-6">
                <p className="text-xs font-medium uppercase text-gray-500 mb-2">
                  Available Colors
                </p>

                <div className="flex gap-3">
                  {data.variants.map((v) => {
                    const isActive = v._id === variant._id;

                    return (
                      <button
                        key={v._id}
                        onClick={() => setVariant(v)}
                        className={`w-9 h-9 rounded-full flex items-center justify-center ${isActive ? "border border-[#0E1422]" : ""
                          }`}
                      >
                        <span
                          style={{ backgroundColor: v.colorCode || "#ccc" }}
                          className={`block w-6 h-6 rounded-full ${isActive ? "ring-2 ring-white" : ""
                            }`}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* SIZES */}
            {hasValidSizes(variant.sizes) && (
              <div className="mb-7">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs font-medium uppercase text-[#5C5F6A]">
                    Select Size
                  </p>

                  <button
                    onClick={() => setOpenSizeChart(true)}
                    className="text-xs font-medium text-black underline"
                  >
                    Size Chart
                  </button>
                </div>

                <div className="flex gap-2">
                  {variant.sizes
                    .filter((s) => s.size && s.size.trim() !== "")
                    .map((s) => (
                      <button
                        key={s._id}
                        onClick={() => setSize(s)}
                        className={`w-11 h-11 rounded-md border text-sm font-medium ${size?._id === s._id
                            ? "border-[#007BFF] text-[#007BFF]"
                            : "border-gray-300 text-gray-700"
                          }`}
                      >
                        {s.size}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* BUY BUTTONS */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleBuyNow}
                className="flex-1 bg-[#007BFF] hover:bg-blue-700 text-white py-3 rounded-lg font-medium"
              >
                Buy Now
              </button>


              <button
                onClick={handleAddToCart}
                className="w-12 h-12 border-[1px] border-[#007BFF] rounded-lg flex items-center justify-center text-[#007BFF]">
                <ShoppingCart size={18} className="text-black" />
              </button>
            </div>

            {/* Delivery Icons */}
            <div className="grid grid-cols-3 gap-4 mt-4 mb-6">
              <div className="bg-gray-50 rounded-2xl p-4 flex flex-col items-center justify-center gap-2 text-center">
                <div className="w-10 h-10 flex items-center justify-center">
                  <img
                    src={freeDelivery}
                    alt=""
                    className="w-10 h-10"
                  />
                </div>
                {/* Free Delivery Condition */}
                <p className="text-sm font-medium text-gray-700">
                  Free Delivery <br />
                  <span className="text-xs text-gray-500">on orders above ₹1000</span>
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-4 flex flex-col items-center justify-center gap-2 text-center">
                <div className="w-10 h-10 flex items-center justify-center">
                  <img
                    src={freeReturn}
                    alt=""
                    className="w-10 h-10"
                  />
                </div>
                <p className="text-sm font-medium text-gray-700">
                  Easy<br />Free Returns
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-4 flex flex-col items-center justify-center gap-2 text-center">
                <div className="w-10 h-10 flex items-center justify-center">
                  <img
                    src={securePay}
                    alt=""
                    className="w-10 h-10"
                  />
                </div>
                <p className="text-sm font-medium text-gray-700">Secure Payments</p>
              </div>
            </div>


            {/* Coupons & Offers */}
            {/* <div className="border border-gray-200 rounded-xl p-5 text-sm">
        <p className="font-semibold text-gray-800 mb-3">
            Coupons & Offers
        </p>

        <ul className="space-y-2 text-gray-600">
            <li>Get Flat 10% Discount With Karur Vysya Bank Bank's VISA Cards</li>
            <li>Get Flat 10% Discount With IndusInd Bank Debit Cards</li>
            <li>Get Flat 10% Discount With IDBI Bank Credit Or Debit Cards</li>
            <li>Get 5% Discount With AU Bank Credit Card</li>
            <li>Get Upto ₹50 Cashback On Using Amazon Pay Balance</li>
        </ul>

        <button className="mt-3 text-sm text-blue-600 font-medium">
            View all coupons
        </button>
        </div> */}
          </div>
        </div>

        {/* TABS */}
        {/* TABS */}
        <div className="flex gap-4 mt-12">
          <button
            onClick={() => setActiveTab("overview")}
            className={`px-6 py-2 rounded-lg font-medium ${activeTab === "overview"
                ? "bg-blue-50 text-[#007BFF]"
                : "bg-gray-100"
              }`}
          >
            Product Overview
          </button>

          <button
            onClick={() => setActiveTab("reviews")}
            className={`px-6 py-2 rounded-lg font-medium ${activeTab === "reviews"
                ? "bg-blue-50 text-[#007BFF]"
                : "bg-gray-100"
              }`}
          >
            Rating & Reviews
          </button>
        </div>


        {/* BOTTOM CONTENT */}
        {activeTab === "overview" &&
          <div className="grid lg:grid-cols-2 gap-8 mt-6">
            {/* Left */}
            {hasValue(data.product.description) && (
              <div className="border rounded-xl p-4 text-sm">
                <p className="font-medium mb-3">Additional Information</p>
                <p className="text-gray-600">{data.product.description}</p>
              </div>
            )}

            {/* Right */}
            {hasValue(data.product.highlights) && (
              <div className="border rounded-xl p-4 text-sm">
                <p className="font-medium mb-3">Highlights</p>
                <div className="grid grid-cols-2 gap-y-2 text-gray-600">
                  {data.product.highlights.map((h, i) => (
                    <React.Fragment key={i}>
                      <span>{h.key}</span>
                      <span>{h.value}</span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        }
        {/* REVIEWS SECTION */}
        {/* {activeTab === "reviews" && (
  <div className="mt-8">

    <h2 className="text-lg font-semibold mb-4">
      Reviews & Ratings
    </h2>

    
    {reviews.length === 0 && (
      <p className="text-sm text-gray-500">
        No reviews yet. Be the first to review this product.
      </p>
    )}

    
    { reviews.length > 0 &&  (
      <>
      <div className="grid lg:grid-cols-[300px_1fr] gap-6 mb-8">

          
          <div className="bg-[#F9FAFB] rounded-xl p-6 flex flex-col items-center justify-center">
            <h3 className="text-4xl font-bold text-[#191C1F]">
              {reviewsSummary.rating}
            </h3>

            <div className="flex gap-1 my-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <TiStarFullOutline
                  key={i}
                  className={`w-5 h-5 ${
                    i < Math.round(reviewsSummary.rating)
                      ? "text-[#FFA439]"
                      : "text-gray-300"
                  }`}
                />
              ))}
            </div>

            <p className="text-sm text-[#4F547B]">
              Product Rating
            </p>
          </div>

          
          <div className="bg-[#F9FAFB] rounded-xl p-6 space-y-4">
            {reviewsSummary.breakdown.map((b) => (
              <div key={b.star} className="flex items-center gap-3">
                <span className="text-sm w-6 text-[#191C1F]">
                  {b.star}
                </span>

                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-[#007BFF] h-2 rounded-full"
                    style={{ width: `${b.percent}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

        </div>
        </>
    )}


   
    <div className="space-y-6">
      {reviews.map((r) => (
        <div key={r.id} className="border-b pb-6">

          <div className="flex gap-4">
            
            <div className="w-10 h-10 rounded-full bg-[#007BFF] text-white flex items-center justify-center font-medium">
              {r.name[0]}
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-3">
                <p className="font-medium text-gray-900">
                  {r.name}
                </p>
                <span className="text-xs text-gray-400">
                  {r.time}
                </span>
              </div>

              <div className="flex gap-1 my-1">
                {Array.from({ length: r.rating }).map((_, i) => (
                  <span key={i} className="text-[#007BFF] text-sm">
                    <TiStarFullOutline className="text-[#FFA439] w-5 h-5"/>
                  </span>
                ))}
              </div>

              <p className="font-medium text-sm mb-1">
                {r.title}
              </p>

              <p className="text-sm text-gray-600">
                {r.comment}
              </p>

              <div className="flex gap-4 text-xs text-gray-400 mt-3">
                <button>Like</button>
                <button>Reply</button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>

    <button className="mt-6 text-sm text-blue-600 font-medium">
      View All Reviews
    </button>
    
  </div>
)} */}
        {/* REVIEWS SECTION */}
        {activeTab === "reviews" && (
          <div className="mt-8">

            <h2 className="text-lg font-semibold mb-4">
              Reviews & Ratings
            </h2>

            {/* EMPTY STATE */}
            {reviews.length === 0 && (
              <p className="text-sm text-gray-500">
                No reviews yet. Be the first to review this product.
              </p>
            )}

            {/* SUMMARY */}
            {reviews.length > 0 && (
              <div className="grid lg:grid-cols-[300px_1fr] gap-6 mb-8">

                {/* LEFT RATING */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 flex flex-col items-center justify-center">
                  <h3 className="text-4xl font-bold text-[#191C1F]">
                    {reviewsSummary.rating}
                  </h3>

                  <div className="flex gap-1 my-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <TiStarFullOutline
                        key={i}
                        className={`w-5 h-5 ${i < Math.round(reviewsSummary.rating)
                            ? "text-[#FFA439]"
                            : "text-gray-300"
                          }`}
                      />
                    ))}
                  </div>

                  <p className="text-sm text-[#4F547B]">
                    Product Rating
                  </p>
                </div>

                {/* RIGHT DISTRIBUTION */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 space-y-4">
                  {reviewsSummary.breakdown.map((b) => (
                    <div key={b.star} className="flex items-center gap-3">
                      <span className="text-sm w-6 text-[#191C1F]">
                        {b.star}
                      </span>

                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-[#007BFF] h-2 rounded-full"
                          style={{ width: `${b.percent}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            )}

            {/* REVIEWS LIST */}
            <div className="space-y-6">
              {reviews.map((r) => (
                <div key={r._id} className="border-b pb-6">
                  <div className="flex gap-4">

                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-[#007BFF] text-white flex items-center justify-center font-medium">
                      {r.userId?.name?.[0] || "U"}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <p className="font-medium text-gray-900">
                          {r.userId?.name || "User"}
                        </p>
                        <span className="text-xs text-gray-400">
                          {new Date(r.createdAt).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="flex gap-1 my-1">
                        {Array.from({ length: r.rating }).map((_, i) => (
                          <TiStarFullOutline
                            key={i}
                            className="text-[#FFA439] w-5 h-5"
                          />
                        ))}
                      </div>

                      <p className="text-sm text-gray-600">
                        {r.comment}
                      </p>

                      {/* REVIEW IMAGES */}
                      {r.images?.length > 0 && (
                        <div className="flex gap-2 mt-3">
                          {r.images.map((img, i) => (
                            <img
                              key={i}
                              src={img}
                              alt=""
                              className="w-16 h-16 object-fill rounded-md border"
                            />
                          ))}
                        </div>
                      )}
                      <div className="flex gap-4 text-xs text-gray-400 mt-3">
                        <button className="flex items-center"><SlLike className="w-3 h-3 mr-1" />Like</button>
                        <button className="flex items-center"><MdOutlineQuickreply className="w-3 h-3 mr-1" />Reply</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <SimilarProducts productId={productId} title="Similar Products" showViewAll={true} />
        </div>
        <div>
          <SimilarProducts productId={productId} title="You might also like" showViewAll={true} />
        </div>
        {openSizeChart && (
          <SizeChartModal onClose={() => setOpenSizeChart(false)} sizeChartData={sizeChartData} />
        )}

        {/* Share Modal */}
        <ShareModal
          isOpen={openShare}
          onClose={() => setOpenShare(false)}
          productUrl={productUrl}
          productName={data.product.productName}
        />

      </div>
    </div>
  );

};

export default ProductDetails;